import type { EntitlementFormData, OrgEntitlementFormData, Product } from '../../types';

export type EntitlementImportMode = 'standard' | 'org';
export type ImportedEntitlement = EntitlementFormData | OrgEntitlementFormData;

export interface EntitlementImportResult {
  rows: ImportedEntitlement[];
  errors: string[];
  warnings?: string[];
}

export interface EntitlementImportOptions {
  products?: Product[];
  defaultProductId?: number;
  defaultProductName?: string;
}

const parseRecords = (input: string, delimiter: string): string[][] => {
  const records: string[][] = [];
  let record: string[] = [];
  let field = '';
  let quoted = false;

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    if (char === '"') {
      if (quoted && input[index + 1] === '"') {
        field += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === delimiter && !quoted) {
      record.push(field.trim());
      field = '';
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && input[index + 1] === '\n') {index += 1;}
      record.push(field.trim());
      if (record.some((value) => value.length > 0)) {records.push(record);}
      record = [];
      field = '';
    } else {
      field += char;
    }
  }

  if (quoted) {throw new Error('A quoted CSV field is not closed.');}
  record.push(field.trim());
  if (record.some((value) => value.length > 0)) {records.push(record);}
  return records;
};

const normalizeHeader = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const headerIndex = (headers: string[], aliases: string[]): number => {
  const normalizedAliases = aliases.map(normalizeHeader);
  return headers.findIndex((header) => normalizedAliases.includes(normalizeHeader(header)));
};

const readBoolean = (value: string, defaultValue: boolean, rowNumber: number, label: string, errors: string[]): boolean => {
  if (!value) {return defaultValue;}
  const normalized = value.trim().toLowerCase();
  if (['true', 'yes', '1', 'y'].includes(normalized)) {return true;}
  if (['false', 'no', '0', 'n'].includes(normalized)) {return false;}
  errors.push(`Row ${rowNumber}: ${label} must be true/false, yes/no, or 1/0.`);
  return defaultValue;
};

const normalizeAccessType = (value: string): 'R' | 'RW' | '' => {
  const normalized = value.toUpperCase().replace(/[^A-Z]/g, '');
  if (['R', 'READ', 'READONLY'].includes(normalized)) {return 'R';}
  if (['RW', 'READWRITE'].includes(normalized)) {return 'RW';}
  return '';
};

/** Parse entitlement rows from a CSV/TSV file or pasted spreadsheet data. */
export const parseEntitlementCSV = (
  input: string,
  mode: EntitlementImportMode,
  options: EntitlementImportOptions = {}
): EntitlementImportResult => {
  const cleanInput = input.replace(/^\uFEFF/, '').trim();
  if (!cleanInput) {return { rows: [], errors: ['Paste CSV content or choose a CSV file first.'] };}

  const delimiter = (cleanInput.split(/\r?\n/, 1)[0] || '').includes('\t') ? '\t' : ',';
  let records: string[][];
  try {
    records = parseRecords(cleanInput, delimiter);
  } catch (error) {
    return { rows: [], errors: [error instanceof Error ? error.message : 'Could not parse this CSV.'] };
  }

  if (records.length < 2) {return { rows: [], errors: ['CSV needs a header row and at least one entitlement row.'] };}

  const headers = records[0];
  const productIndex = headerIndex(headers, ['product', 'product name', 'product id', 'product key']);
  const nameIndex = headerIndex(headers, ['name', 'entitlement name', 'entitlement_name']);
  const valueIndex = headerIndex(headers, ['value', 'entitlement value', 'entitlement_value']);
  const descriptionIndex = headerIndex(headers, ['description', 'entitlement description', 'entitlement_description', 'desc']);
  const quantityIndex = headerIndex(headers, ['needs_quantity', 'needs quantity', 'needs qty', 'quantity required']);
  const unitIndex = headerIndex(headers, ['unit', 'quantity unit']);
  const requireDescriptionIndex = headerIndex(headers, ['require_description', 'require description', 'description required']);
  const accessTypeIndex = headerIndex(headers, ['access_type', 'access type', 'permission']);
  const serviceIndex = headerIndex(headers, ['is_service', 'is service', 'service', 'service flag']);
  const errors: string[] = [];
  const warnings: string[] = [];

  if (nameIndex < 0) {errors.push('CSV needs a Name column.');}
  if (valueIndex < 0) {errors.push('CSV needs a Value column.');}
  if (productIndex >= 0 && options.defaultProductId === undefined) {
    errors.push('Select a product in the wizard before importing a CSV with a Product column.');
  }
  if (errors.length > 0) {return { rows: [], errors };}

  const rows: ImportedEntitlement[] = [];
  const seen = new Set<string>();
  let skippedProductRows = 0;
  let matchedProductRows = 0;
  records.slice(1).forEach((record, index) => {
    const rowNumber = index + 2;
    const valueAt = (columnIndex: number): string => (columnIndex >= 0 ? (record[columnIndex] || '').trim() : '');
    const productReference = valueAt(productIndex);

    let productId: number | undefined;
    if (productIndex >= 0) {
      if (!productReference) {
        errors.push(`Row ${rowNumber}: Product is required when the CSV includes a Product column.`);
        return;
      }
      const normalizedProductReference = productReference.trim().toLocaleLowerCase();
      const matches = (options.products || []).filter((product) =>
        String(product.id ?? '').toLocaleLowerCase() === normalizedProductReference ||
        (product.name || '').trim().toLocaleLowerCase() === normalizedProductReference ||
        (product.key || '').trim().toLocaleLowerCase() === normalizedProductReference
      );
      const matchesDefaultProduct = options.defaultProductId !== undefined &&
        (String(options.defaultProductId).toLocaleLowerCase() === normalizedProductReference ||
          (options.defaultProductName || '').trim().toLocaleLowerCase() === normalizedProductReference);
      if (matches.length > 1) {
        errors.push(`Row ${rowNumber}: Product "${productReference}" is ambiguous. Use its ID or unique key.`);
        return;
      }
      if (matches.length === 1 && matches[0].id !== undefined) {
        productId = matches[0].id;
      } else if (matchesDefaultProduct) {
        productId = options.defaultProductId;
      } else {
        // The wizard imports into one selected product. Treat other product references
        // as out-of-scope rows so they can be skipped with a clear warning below.
        skippedProductRows += 1;
        return;
      }
      if (Number(productId) !== Number(options.defaultProductId)) {
        skippedProductRows += 1;
        return;
      }
      matchedProductRows += 1;
    }

    const name = valueAt(nameIndex);
    const value = valueAt(valueIndex);
    if (!name) {errors.push(`Row ${rowNumber}: Name is required.`);}
    if (!value) {errors.push(`Row ${rowNumber}: Value is required.`);}
    if (!name || !value) {return;}

    const duplicateScope = productId !== undefined
      ? String(productId)
      : String(options.defaultProductId ?? 'current-product');
    const duplicateKey = `${duplicateScope}\u0000${name.toLocaleLowerCase()}\u0000${value.toLocaleLowerCase()}`;
    if (seen.has(duplicateKey)) {
      errors.push(`Row ${rowNumber}: duplicate entitlement Name/Value pair in this import.`);
      return;
    }
    seen.add(duplicateKey);

    const description = valueAt(descriptionIndex);
    const needsQuantity = readBoolean(valueAt(quantityIndex), false, rowNumber, 'Needs Quantity', errors);
    const requireDescription = readBoolean(valueAt(requireDescriptionIndex), true, rowNumber, 'Require Description', errors);
    const base = {
      name,
      value,
      ...(productId !== undefined ? { productId } : {}),
      description,
      needs_quantity: needsQuantity,
      unit: valueAt(unitIndex),
      requireDescription,
    };

    if (mode === 'org') {
      const rawAccessType = valueAt(accessTypeIndex);
      const accessType = rawAccessType ? normalizeAccessType(rawAccessType) : 'RW';
      if (!accessType) {errors.push(`Row ${rowNumber}: Access Type must be R/Read Only or RW/Read Write.`);}
      const isService = readBoolean(valueAt(serviceIndex), false, rowNumber, 'Is Service', errors);
      rows.push({ ...base, access_type: accessType || 'RW', is_service: isService } as OrgEntitlementFormData);
    } else {
      rows.push(base as EntitlementFormData);
    }
  });

  if (productIndex >= 0 && matchedProductRows === 0) {
    const selectedProduct = options.defaultProductName || String(options.defaultProductId);
    errors.push(`None of the entitlement rows are associated with the product selected in the wizard ("${selectedProduct}").`);
  }
  if (skippedProductRows > 0) {
    const selectedProduct = options.defaultProductName || String(options.defaultProductId);
    warnings.push(`${skippedProductRows} row${skippedProductRows === 1 ? '' : 's'} for other products will be skipped. Only entitlements for "${selectedProduct}" will be imported.`);
  }

  return errors.length > 0 ? { rows: [], errors, warnings } : { rows, errors: [], warnings };
};
